import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Project {
  id: number;
  title: string;
  category: string;
  image: string;
  description: string;
}

const categories = [
  'All',
  'Custom Fashion',
  'Ready-to-Wear',
  'Bridal',
  'Sustainable',
  'Runway',
  'Textile'
];

const projects: Project[] = [
  {
    id: 1,
    title: "Luxury Evening Gown Collection",
    category: "Custom Fashion",
    image: "https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    description: "Bespoke couture designs for red carpet events"
  },
  {
    id: 2,
    title: "Spring/Summer Collection 2024",
    category: "Ready-to-Wear",
    image: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    description: "Latest seasonal fashion trends"
  },
  {
    id: 3,
    title: "Ethereal Bridal Collection",
    category: "Bridal",
    image: "https://images.unsplash.com/photo-1595777457583-95e059d581b8?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    description: "Exclusive wedding gowns and accessories"
  },
  {
    id: 4,
    title: "Eco-Conscious Fashion Line",
    category: "Sustainable",
    image: "https://images.unsplash.com/photo-1581375074612-d1fd0e661aeb?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    description: "Sustainable and ethical fashion pieces"
  },
  {
    id: 5,
    title: "Paris Fashion Week 2024",
    category: "Runway",
    image: "https://images.unsplash.com/photo-1509319117193-57bab727e09d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    description: "Exclusive runway collection showcase"
  },
  {
    id: 6,
    title: "Artisanal Textile Collection",
    category: "Textile",
    image: "https://images.unsplash.com/photo-1544441892-794166f1e3be?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    description: "Custom fabric designs and patterns"
  },
  {
    id: 7,
    title: "Celebrity Red Carpet Designs",
    category: "Custom Fashion",
    image: "https://images.unsplash.com/photo-1518049362265-d5b2a6467637?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    description: "Bespoke designs for high-profile events"
  },
  {
    id: 8,
    title: "Autumn Essentials",
    category: "Ready-to-Wear",
    image: "https://images.unsplash.com/photo-1539109136881-3be0616acf4b?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    description: "Seasonal ready-to-wear collection"
  },
  {
    id: 9,
    title: "Sustainable Denim Collection",
    category: "Sustainable",
    image: "https://images.unsplash.com/photo-1582719508461-905c673771fd?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    description: "Eco-friendly denim innovations"
  },
  {
    id: 10,
    title: "Luxury Wedding Collection",
    category: "Bridal",
    image: "https://images.unsplash.com/photo-1594552072238-b8a33785b261?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    description: "Exclusive bridal couture pieces"
  }
];

const Portfolio = () => {
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredProjects = activeCategory === 'All'
    ? projects
    : projects.filter(project => project.category === activeCategory);

  return (
    <section className="py-24 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">Our Collections</h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Explore our diverse portfolio of fashion designs, from bespoke couture to sustainable collections.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <motion.button
              key={category}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveCategory(category)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
                activeCategory === category
                  ? 'bg-emerald-500 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              {category}
            </motion.button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence mode="wait">
            {filteredProjects.map((project) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.3 }}
                className="group relative overflow-hidden rounded-lg bg-gray-900"
              >
                <div className="relative h-[300px] overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 p-6">
                      <h3 className="text-xl font-bold mb-2">{project.title}</h3>
                      <p className="text-gray-300">{project.description}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;